import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Typography } from '@material-ui/core';
import { UserBlockAnswers } from '../../../../../../config/api/models/Answers';
import { Message, ProfilePicture } from '../../../../../shared/components';
import { roundToDecimals } from '../../../../../shared/math';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { setSelectedStudent } from 'src/modules/shared/slices/filters/entity/FiltersSlice';
import {
  AverageRow,
  AverageTitle,
  Col,
  DataGrid,
  Row,
  StyledListItem,
  StyledListItemWrapper,
  UserDetails,
  UserName,
} from './Atoms';
import { useGetStudentByGroupQuery } from 'src/modules/shared/slices/student/api/students';

type Props = {
  data: UserBlockAnswers[];
};


const GroupAnswers: FC<Props> = ({ data }) => {
  const studentAnswers = useMemo(() => Object.assign({}, ...data.map((item) => ({[item.studentId]: item}))), [data]);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const {
    data: studentsData,
    isError: studentsIsError,
    isSuccess: studentsIsSuccess,
    isLoading: studentsIsLoading,
  } = useGetStudentByGroupQuery(selectedGroup || '');

  const averageNumAttempts = useMemo(() => {
    if (data[0]?.numAttempts == null) return '';

    let result = 0;
    for (let i = 0; i < data.length; i++) {
      result += data[i]?.numAttempts || 0;
    }
    return roundToDecimals(result / data.length, 1)
      .toString()
      .replace('.', ',');
  }, [data]);

  const averageScore = useMemo(() => {
    if (data.length === 0) return '';
    if (data[0].attempts?.last?.score == null) return '';

    let result = 0;
    for (let i = 0; i < data.length; i++) {
      result += data[i].attempts?.last?.score || 0;
    }

    return `${roundToDecimals(result / data.length, 1)
      .toString()
      .replace('.', ',')}/${data[0].attempts.last.maxScore}`;
  }, [data]);

  const handleClickStudent = useCallback(
    (studentId: string) => {
      dispatch(setSelectedStudent(studentId));
    },
    [dispatch]
  );

  if (studentsIsLoading) {
    return <CircularProgress />;
  }

  if (studentsIsError) {
    return <Message type="error">{t('trajectory.sections.shared.answers.error')}</Message>;
  }

  if (studentsIsSuccess && studentsData?.length === 0) {
    return <Message type="info">{t('trajectory.sections.shared.answers.groupHasNoStudents')}</Message>;
  }

  return <>
    {studentsIsSuccess && (<DataGrid>
      <Row className="header">
        <Col>
          <Typography variant="h4">
            {t('trajectory.sections.shared.answers.numAttempts')}
          </Typography>
        </Col>
        <Col>
          <Typography variant="h4">
            {t('trajectory.sections.shared.answers.lastScore')}
          </Typography>
        </Col>
      </Row>
      {studentsData.map((student) => {
        const answer = studentAnswers[student.id];
        
        return (
          <StyledListItemWrapper
            startWithGrey
            key={student.id}
            onClick={() => handleClickStudent(student.id)}
          >
            <StyledListItem className="listItem">
              <Row>
                <Col>
                  <UserDetails>
                    <ProfilePicture size="small" initials={student.initials} />
                    <UserName>{`${student.fullName}`}</UserName>
                  </UserDetails>
                </Col>
                <Col>{answer && answer.numAttempts ? answer.numAttempts : ''}</Col>
                <Col
                  className={
                    answer &&
                    answer.attempts &&
                    answer.attempts.last.score / answer.attempts.last.maxScore < 0.5
                      ? 'warning'
                      : ''
                  }
                >
                  {answer && answer.attempts && (
                    <>
                      {answer.attempts.last.score}/{answer.attempts.last.maxScore}
                    </>
                  )}
                </Col>
              </Row>
            </StyledListItem>
          </StyledListItemWrapper>
        );
      })}
      <AverageRow>
        <AverageTitle variant="h3">
          {t('trajectory.sections.shared.answers.groupAverage')}
        </AverageTitle>
        <Col className="average">{averageNumAttempts}</Col>
        <Col className="average">{averageScore}</Col>
      </AverageRow>
    </DataGrid>)}
  </>
};

export default GroupAnswers;
